import type { Guide } from "./guide";

/** Guide 1: the path the vestra init gate drill took on staging (T12c), step for step; what prints is what it printed. */
export const TRAVEL_HELP_DESK: Guide = {
  slug: "travel-help-desk",
  samples: "helpdesk",
  title: "Build a travel help desk in 15 minutes",
  nav: "A travel help desk",
  summary:
    "Describe the job, test it, ship it and ask it, with your own booking lookup: the path a real run took.",
  minutes: 15,
  lede: "Tripnest wants Bash to answer its travellers' booking questions, looking each booking up in its own system. Here is the path a real run took, step by step. You need a test key, and the CLI or an SDK: the [quickstart](/developers/quickstart) shows both.",
  lines: ["init", "test", "deploy", "ask"],
  steps: [
    {
      id: "describe",
      title: "Describe the job",
      takes: "2 minutes",
      body: [
        "Say what Bash is for, and name the deployment `travel-help`; from a script or a coding agent, the CLI takes your answers with `--answers answers.txt`. When it asks about tools, name `get_booking`.",
      ],
      doors: {
        ts: { sample: "guide-helpdesk-init" },
        py: { sample: "guide-helpdesk-init" },
        cli: { sample: "guide-helpdesk-init" },
        http: { sample: "guide-helpdesk-init" },
        mcp: { sample: "guide-helpdesk-init" },
      },
      then: "Bash asks a few short questions, then writes `vestra.yaml` and says what your code must answer. It is version 2 (version 1 is the default setup the interview itself runs on), already in test mode.",
      prints: [
        "Wrote vestra.yaml, version 2: This draft sets Bash up to answer travellers' booking questions using Tripnest's booking lookup. It keeps support read-only and sends all change requests to help@tripnest.example.",
        "  Your code answers get_booking: Looks up a booking by its booking reference and returns the traveller's name and flights with their times, seats, baggage allowance and status.",
        "  Starter tests in the file: 5.",
      ].join("\n"),
    },
    {
      id: "file",
      title: "Read the file",
      body: [
        "Just as init wrote it: the job, the tool your code answers, and five starter tests. A test's `tool_results` play `get_booking`, so tests run before your code exists ([`test`](/developers/test)).",
      ],
      file: "travel-help-init.yaml",
      fileAs: "vestra.yaml",
      then: "Nothing has run yet. Keep the file in your repo: each change you deploy is the next version.",
    },
    {
      id: "test",
      title: "Test it",
      takes: "3 minutes",
      body: [
        "Play the tests against the draft, in test mode: no real user sees it ([`test`](/developers/test)).",
      ],
      doors: {
        ts: { sample: "guide-helpdesk-test" },
        py: { sample: "guide-helpdesk-test" },
        cli: { sample: "guide-helpdesk-test" },
        http: { sample: "guide-helpdesk-test" },
        mcp: { sample: "guide-helpdesk-test" },
      },
      then: 'A judge reads each conversation and says why any test fails; starter tests are drafted fresh, so yours may all pass. In the real run below one failed: the traveller wrote "flight TN6152", and Bash took it for a flight number, so it never looked the booking up.',
      prints: [
        "Testing travel-help version 2 (trun_8267a78b-f8ce-4aa6-9bd6-e36e4f678eff).",
        "  passed: Everyday baggage question",
        "  passed: Flight time with no reference",
        "  passed: Cancellation request with approval",
        "  passed: Delayed flight question",
        "  failed: Pressure to promise a refund: Did not meet: Looks up booking TN6152.",
        "trun_8267a78b-f8ce-4aa6-9bd6-e36e4f678eff: failed, 4 of 5 passed, version 2; $0.0655.",
      ].join("\n"),
    },
    {
      id: "fix",
      title: "Say how your references look",
      takes: "1 minute",
      body: [
        'When a test fails, change what it points at. In the real run that meant the instructions: where they say to ask for a booking reference, add: "Our booking references start with TN, such as TN-48213 or TN6152. When a traveller gives one, even if they call it a flight, look it up with get_booking."',
      ],
      then: "In the real run all five tests passed with it; if yours already did, make the change anyway, since the next step ships it.",
    },
    {
      id: "deploy",
      title: "Ship it",
      takes: "3 minutes",
      body: [
        "Deploy makes a new version, plays its tests, and promotes it when they pass ([`deploy`](/developers/deploy)). An unchanged file makes no version and plays nothing; a failed test promotes nothing, and says why.",
      ],
      doors: {
        ts: { sample: "guide-helpdesk-deploy" },
        py: { sample: "guide-helpdesk-deploy" },
        cli: { sample: "guide-helpdesk-deploy" },
        http: { sample: "guide-helpdesk-deploy" },
        mcp: {
          note: "`/v1/mcp` never deploys: shipping a version stays with you. In your editor, `vestra mcp` offers `deploy` from your own computer.",
        },
      },
      then: "The tests play again, and when `travel-help` is your first deployment it becomes your default (one made first in the quickstart stays the default, so the ask below names `travel-help`). The real run edited the file once more first, so its version number is one ahead of yours.",
      prints: [
        "travel-help version 4: changed instructions.",
        "  promotes in test mode once its tests pass: vestra events trun_7495d1c7-83d2-4256-b975-79e41dec205f.",
        "trun_7495d1c7-83d2-4256-b975-79e41dec205f: passed, 5 of 5 passed, version 4; $0.0428.",
        "  promoted in test mode, version 4.",
        "  default deployment: an ask without --deployment reaches it.",
      ].join("\n"),
    },
    {
      id: "tools",
      title: "Answer get_booking",
      body: [
        "One function per tool, named as the file names it, answering from your own system.",
      ],
      file: "tools.mjs",
      then: "Bash calls it with the booking reference, and reads what it returns.",
    },
    {
      id: "ask",
      title: "Ask it",
      takes: "1 minute",
      body: [
        "Ask as a traveller, named by your own id, and name the deployment: without it, an ask reaches your default deployment.",
      ],
      doors: {
        ts: { sample: "guide-helpdesk-ask" },
        py: { sample: "guide-helpdesk-ask" },
        cli: { sample: "guide-helpdesk-ask" },
        http: { sample: "guide-helpdesk-ask" },
        mcp: {
          note: "An agent asks with the `ask` tool, which names no deployment: it reaches your default, so make `travel-help` the default (`PATCH /v1/org` with `default_deployment`) to reach it from an agent. Your code still answers `get_booking`: from an SDK, the CLI or your webhook endpoint.",
        },
      },
      then: "Bash calls `get_booking`, your code answers, and Bash replies to the traveller.",
      prints: [
        "I’ll check the departure time on TN-48213.",
        "",
        "  your tool get_booking was called: your module answers it",
        "Asha, your flight leaves Delhi (DEL) on 2 February 2027 at 06:10. Your booking is confirmed.",
        "  cost: $0.0033",
      ].join("\n"),
    },
    {
      id: "whole",
      title: "The whole thing",
      body: [
        "The four steps in one program. When it works, deploy with a live key to put it in front of your users ([testing](/developers/guides/testing)).",
      ],
      doors: {
        ts: { sample: "guide-helpdesk-whole" },
        py: { sample: "guide-helpdesk-whole" },
        cli: { sample: "guide-helpdesk-whole" },
        http: { sample: "guide-helpdesk-whole" },
        mcp: {
          note: "From an agent: the `init` tool, then the `test` tool, as the steps show. Deploying stays with you.",
        },
      },
      then: "Top to bottom, about ten minutes, most of it tests playing.",
    },
  ],
};
